import { useState, useCallback } from 'react';
import {
  api,
  type AdminAnalyticsUserDetail,
  type AdminUserLookupResult,
  type AnalyticsPeriod,
} from '@/lib/api';
import { SuspendUserDialog } from './SuspendUserDialog';
import { PeriodSelector } from './components/PeriodSelector';
import { StatCard } from './components/StatCard';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { BreakdownTable } from './components/BreakdownTable';

interface UsersTabProps {
  initialUserId?: string;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    succeeded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    past_due: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    suspended: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    deleted: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-muted text-muted-foreground'}`}
    >
      {status}
    </span>
  );
}

export function UsersTab({ initialUserId }: UsersTabProps) {
  const [userIdInput, setUserIdInput] = useState(initialUserId ?? '');
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AdminAnalyticsUserDetail | null>(null);
  const [userInfo, setUserInfo] = useState<AdminUserLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);

  const fetchData = useCallback(
    async (overrideUserId?: string, overridePeriod?: AnalyticsPeriod, nocache = false) => {
      const input = (overrideUserId ?? userIdInput).trim();
      const p = overridePeriod ?? period;
      if (!input) return;
      setLoading(true);
      setError(null);
      try {
        // Always resolve via the lookup endpoint so the support-facing
        // identity (username, email, roles, auth method, contributor /
        // payment / devvx flags) is available regardless of which form
        // of identifier the agent pasted.
        const lookup = await api.adminLookupUserByAnyIdentifier(input);
        setUserInfo(lookup);
        const result = await api.adminGetAnalyticsUser(lookup.userId, p, nocache);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user detail');
        setData(null);
        setUserInfo(null);
      } finally {
        setLoading(false);
      }
    },
    [userIdInput, period],
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchData();
            }}
            placeholder="Username, email, or user ID (usr_...)"
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm w-72"
          />
          <button
            onClick={() => fetchData()}
            disabled={loading || !userIdInput.trim()}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Search'}
          </button>
        </div>
        <PeriodSelector
          period={period}
          onChange={(p) => {
            setPeriod(p);
            // Re-fetch with the new period explicitly — setPeriod is
            // async so the closed-over `period` inside fetchData is
            // still stale at this point.
            if (data) fetchData(data.userId, p);
          }}
          onRefresh={() => data && fetchData(data.userId, undefined, true)}
          loading={loading}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {!data && !error && !loading && (
        <div className="bg-muted/30 border border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
          Search by username, email, or user_id to view their activity timeline, balance, sessions,
          and ledger.
        </div>
      )}

      {data && (
        <>
          {userInfo && (
            <div className="bg-card rounded-lg border border-border p-6 mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-3">User Profile</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium text-foreground">{userInfo.displayName || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Username</p>
                  <p className="font-medium text-foreground">{userInfo.username}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground break-all">{userInfo.email || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">User ID</p>
                  <p className="font-mono text-xs text-foreground break-all">{userInfo.userId}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Account Status</p>
                  <div className="flex items-center gap-2">
                    {statusBadge(userInfo.status)}
                    {/*
                      A deleted account offers no action: the transition is
                      terminal in both directions server-side, so showing a
                      button that can only 422 would be a worse answer than
                      showing none.
                    */}
                    {userInfo.status !== 'deleted' && (
                      <button
                        onClick={() => setSuspendDialogOpen(true)}
                        className="text-xs font-medium underline underline-offset-2 text-muted-foreground hover:text-foreground"
                      >
                        {userInfo.status === 'suspended' ? 'Reinstate' : 'Suspend'}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Roles</p>
                  <p className="text-foreground">{userInfo.roles.join(', ') || 'none'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Auth Method</p>
                  <p className="text-foreground capitalize">{userInfo.primaryAuthMethod || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contributor</p>
                  <p className="text-foreground capitalize">{userInfo.contributorStatus || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payments Enabled</p>
                  <p className="text-foreground">
                    {userInfo.paymentEnabled == null ? '—' : userInfo.paymentEnabled ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">DevvX Linked</p>
                  <p className="text-foreground">
                    {userInfo.devvxAccountLinked == null
                      ? '—'
                      : userInfo.devvxAccountLinked
                        ? `Yes${userInfo.devvxAccountId ? ` (${userInfo.devvxAccountId})` : ''}`
                        : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Joined</p>
                  <p className="text-foreground">{formatDate(userInfo.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Updated</p>
                  <p className="text-foreground">{formatDate(userInfo.updatedAt)}</p>
                </div>
              </div>

              {/*
                Only rendered while the suspension is live — the server clears
                all three fields on reinstatement, so this cannot linger and
                describe a suspension that has already been lifted.
              */}
              {userInfo.status === 'suspended' && userInfo.suspendedAt && (
                <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm">
                  <p className="font-medium text-red-700 dark:text-red-400">
                    Suspended {formatDate(userInfo.suspendedAt)}
                  </p>
                  {userInfo.suspensionReason && (
                    <p className="mt-1 text-muted-foreground">{userInfo.suspensionReason}</p>
                  )}
                  {userInfo.suspendedBy && (
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      by {userInfo.suspendedBy}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Balance"
              value={`$${(data.profile.balance / 100).toFixed(2)}`}
              tone="positive"
              hint={`sub: $${(data.profile.subscriptionCreditBalance / 100).toFixed(2)}`}
            />
            <StatCard
              label="Active Days"
              value={data.summary.activeDaysInPeriod}
              hint={`in ${period}`}
            />
            <StatCard
              label="Est. Active Time"
              value={`${data.summary.estimatedActiveMinutes.toLocaleString()} min`}
              hint="heartbeat-derived"
            />
            <StatCard
              label="Period Spend"
              value={`$${(data.summary.totalSpendCredits / 100).toFixed(2)}`}
              hint={`${data.summary.totalInvocations} invocations`}
              tone="warning"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Subscription tier"
              value={data.profile.subscription.tier}
              hint={data.profile.subscription.status}
            />
            <StatCard label="Sessions" value={data.summary.totalSessions} />
            <StatCard
              label="Top arche"
              value={data.summary.topArche ?? '—'}
              hint="most opens this period"
            />
            <StatCard
              label="Lifetime spend"
              value={`$${(data.profile.lifetimeUsed / 100).toFixed(2)}`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Daily Active Time</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Estimated active minutes per day.
              </p>
              <TimeSeriesChart
                data={data.dailyActivity}
                series={[
                  { dataKey: 'estimatedActiveMinutes', label: 'Est. min', color: '#22c55e' },
                ]}
                height={180}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Daily Spend</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Dollars spent on paid invocations per day.
              </p>
              <TimeSeriesChart
                data={data.dailyActivity.map((d) => ({
                  ...d,
                  spendDollars: d.spendCredits / 100,
                }))}
                series={[{ dataKey: 'spendDollars', label: 'Spend ($)', color: '#f59e0b' }]}
                height={180}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">Recent AI Invocations</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Last 20 entity invocations across all surfaces.
              </p>
              <BreakdownTable
                rows={data.recentInvocations}
                emptyMessage="No AI invocations."
                columns={[
                  {
                    key: 'startedAt',
                    label: 'When',
                    render: (r) => (
                      <span className="text-xs text-muted-foreground font-mono">
                        {new Date(r.startedAt).toLocaleString()}
                      </span>
                    ),
                  },
                  {
                    key: 'entityId',
                    label: 'Entity',
                    render: (r) => <span className="text-xs font-mono">{r.entityId}</span>,
                  },
                  {
                    key: 'verb',
                    label: 'Verb',
                    render: (r) => <span className="text-xs">{r.verb}</span>,
                  },
                  {
                    key: 'outcome',
                    label: 'Outcome',
                    render: (r) => (
                      <span
                        className={`text-xs ${
                          r.outcome === 'success'
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : r.outcome === 'failure' || r.outcome === 'error'
                              ? 'text-destructive'
                              : 'text-muted-foreground'
                        }`}
                        title={r.errorCode ?? undefined}
                      >
                        {r.outcome}
                      </span>
                    ),
                  },
                  {
                    key: 'costCents',
                    label: 'Cost',
                    align: 'right',
                    render: (r) => (r.costCents ? `$${(r.costCents / 100).toFixed(4)}` : '—'),
                  },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">
                Recent Builder Sessions
              </h2>
              <BreakdownTable
                rows={data.recentBuilderSessions}
                emptyMessage="No builder sessions."
                columns={[
                  {
                    key: 'archId',
                    label: 'Arche',
                    render: (r) => <span className="text-xs font-mono">{r.archId ?? '—'}</span>,
                  },
                  { key: 'status', label: 'Status' },
                  {
                    key: 'totalCostCents',
                    label: 'Cost',
                    align: 'right',
                    render: (r) =>
                      r.totalCostCents ? `$${(r.totalCostCents / 100).toFixed(2)}` : '—',
                  },
                ]}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Event Timeline</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Last 100 events for this user, most recent first. Shows the user's journey across the
              platform.
            </p>
            <BreakdownTable
              rows={data.eventTimeline}
              emptyMessage="No events recorded for this user in the period."
              columns={[
                {
                  key: 'occurredAt',
                  label: 'When',
                  render: (r) => (
                    <span className="text-xs text-muted-foreground font-mono">
                      {new Date(r.occurredAt).toLocaleString()}
                    </span>
                  ),
                },
                {
                  key: 'eventType',
                  label: 'Type',
                  render: (r) => <span className="text-xs font-mono">{r.eventType}</span>,
                },
                { key: 'eventName', label: 'Name' },
                {
                  key: 'archeId',
                  label: 'Arche',
                  render: (r) =>
                    r.archeId ? <span className="text-xs font-mono">{r.archeId}</span> : '',
                },
                {
                  key: 'pagePath',
                  label: 'Page',
                  render: (r) =>
                    r.pagePath ? (
                      <span className="text-xs text-muted-foreground">{r.pagePath}</span>
                    ) : (
                      ''
                    ),
                },
              ]}
            />
          </div>
        </>
      )}

      {userInfo && (
        <SuspendUserDialog
          open={suspendDialogOpen}
          onOpenChange={setSuspendDialogOpen}
          user={userInfo}
          suspend={userInfo.status !== 'suspended'}
          // Re-resolve rather than patching local state: the status, the
          // forensic trio and `updatedAt` all move together server-side, and a
          // hand-rolled optimistic update is how those drift apart on screen.
          onSuccess={() => void fetchData(userInfo.userId, undefined, true)}
        />
      )}
    </div>
  );
}
